import React, { useState } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isNilOrEmpty } from 'utils/ramda'
import { loginUserRoutine, verifyTwoFactorLoginRoutine } from 'features/auth/ducks/actions'
import { validateLoginField } from 'features/auth/ducks/schema'
import { getCurrentUser, isUserLoggedIn, selectTwoFactorChallenge } from 'features/auth/ducks/selectors'
import { PATHS, redirect } from 'utils/paths'
import LocalStorageService from 'services/LocalStorageService'
import { KEYS } from 'utils/localStorage'
import { homePathFor, resumableLink } from 'utils/postLoginDestination'

import BackgroundImage from 'assets/images/background_bp.png'
import LogoShort from 'assets/images/bp-logo-orange.png'

const IconArrowRight = () => <span>→</span>
const IconArrowLeft = () => <span>←</span>
const IconCheck = () => <span style={{ color: '#36becd', fontWeight: 'bold' }}>✓</span>

const IconEyeOpen = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#6B7280'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'></path>
    <circle cx='12' cy='12' r='3'></circle>
  </svg>
)

const IconEyeClosed = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#6B7280'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24'></path>
    <line x1='1' y1='1' x2='23' y2='23'></line>
  </svg>
)

const ModernInput = ({
                       label,
                       name,
                       value,
                       type = 'text',
                       onChange,
                       onKeyDown,
                       error,
                       autoFocus,
                       rightElement
                     }) => {
  const isValid = !error && value && value.length > 0

  return (
    <InputGroup>
      <Label htmlFor={name}>{label}</Label>
      <InputWrapper $isValid={isValid} $hasError={!!error}>
        <StyledInput
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={e => onChange(name, e.target.value)}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
        />
        <RightSlot>{rightElement ? rightElement : isValid && <IconCheck />}</RightSlot>
      </InputWrapper>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </InputGroup>
  )
}

const Login = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const location = useLocation()
  const user = useSelector(getCurrentUser)
  const isloggedin = useSelector(isUserLoggedIn)
  const twoFactorChallenge = useSelector(selectTwoFactorChallenge)

  const [values, setValues] = useState({
    email: '',
    password: '',
    code: '',
    recoveryCode: '',
    rememberBrowserWeeks: ''
  })
  const [step, setStep] = useState('email') // 'email' | 'password'
  const [errors, setErrors] = useState({})
  const [showPassword, setShowPassword] = useState(false)
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)

  const handleOnChange = (name, value) => {
    setValues({ ...values, [name]: value })
    if (errors[name]) {
      setErrors({ ...errors, [name]: null })
    }
  }

  const handleNextStep = () => {
    validateLoginField(values)('email', res => {
      if (isNilOrEmpty(res.errors)) {
        if (values.email.includes('sainsburys')) {
          const apiUrl = process.env.REACT_APP_API_URL
          const bareApiUrl = apiUrl.replace(/\/api$/, '')
          window.location.href = `${bareApiUrl}/auth/redirect/sainsbury`
        } else {
          setStep('password')
        }
      } else {
        setErrors({ email: 'Please enter a valid email' })
      }
    })
  }

  const handleSubmit = e => {
    if (e) e.preventDefault()

    if (step === 'twoFactor') {
      const credentialName = useRecoveryCode ? 'recoveryCode' : 'code'
      if (!values[credentialName]) {
        setErrors({ [credentialName]: useRecoveryCode ? 'Recovery code is required' : 'Authentication code is required' })
        return
      }

      dispatch(
        verifyTwoFactorLoginRoutine({
          email: values.email.toLowerCase(),
          password: values.password,
          twoFactorToken: twoFactorChallenge?.twoFactorToken,
          rememberBrowserWeeks: values.rememberBrowserWeeks || undefined,
          [credentialName]: values[credentialName]
        })
      )
      return
    }

    if (!values.password) {
      setErrors({ password: 'Password is required' })
      return
    }

    dispatch(
      loginUserRoutine({
        password: values.password,
        email: values.email.toLowerCase()
      })
    )
  }

  const handlePrevStep = () => {
    setStep('email')
    setErrors({})
    setShowPassword(false)
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      step === 'email' ? handleNextStep() : handleSubmit()
    }
  }

  const fromLocation = location.state?.from
  const fromPath = fromLocation
    ? `${fromLocation.pathname || ''}${fromLocation.search || ''}${fromLocation.hash || ''}`
    : null

  React.useEffect(() => {
    const link = resumableLink(fromPath)
    if (link) LocalStorageService.set(KEYS.redirectPath, link)
  }, [fromPath])

  React.useEffect(() => {
    if (twoFactorChallenge?.providers?.length) {
      setStep('twoFactor')
      setErrors({})
    } else {
      setStep(current => (current === 'twoFactor' ? 'password' : current))
      setValues(current => ({ ...current, code: '', recoveryCode: '' }))
    }
  }, [twoFactorChallenge])

  if (isloggedin && user.type !== 'admin') {
    // If we got here because PrivateRoute bounced an unauthenticated user,
    // honour their original destination so deep / share links survive login.
    return <Navigate to={resumableLink(fromPath) || homePathFor(user)} replace />
  }

  return (
    <PageWrapper>
      <LoginCard>
        <LogoSection>
          <img src={LogoShort} alt='Building Passport logo' />
        </LogoSection>

        <HeaderText>
          {t('login.header')}
          <span>{t('login.company')}</span>
        </HeaderText>

        <LoginForm onSubmit={handleSubmit}>
          <FormContent>
            {step === 'email' && (
              <EmailStep>
                <ModernInput
                  autoFocus={step === 'email'}
                  label={t('login.labels.email')}
                  name='email'
                  value={values.email}
                  onChange={handleOnChange}
                  onKeyDown={handleKeyDown}
                  error={errors.email}
                />
                <PrimaryButton
                  type='button'
                  disabled={isNilOrEmpty(values.email)}
                  onClick={handleNextStep}
                >
                  {t('login.labels.submitButton')} <IconArrowRight />
                </PrimaryButton>
              </EmailStep>
            )}

            {step === 'password' && (
              <PasswordStep>
                <ModernInput
                  autoFocus={step === 'password'}
                  label={t('login.labels.password')}
                  name='password'
                  type={showPassword ? 'text' : 'password'}
                  value={values.password}
                  onChange={handleOnChange}
                  onKeyDown={handleKeyDown}
                  error={errors.password}
                  rightElement={
                    <EyeToggle onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <IconEyeOpen /> : <IconEyeClosed />}
                    </EyeToggle>
                  }
                />

                <ForgotPasswordWrapper>
                  <ForgotPasswordLink onClick={() => redirect(PATHS.requestPasswordReset)}>
                    {t('login.forgotPassword')}
                  </ForgotPasswordLink>
                </ForgotPasswordWrapper>

                <ButtonGroup>
                  <BackButton type='button' onClick={handlePrevStep}>
                    <IconArrowLeft /> Back
                  </BackButton>
                  <PrimaryButton type='submit'>{t('login.labels.submitButton')}</PrimaryButton>
                </ButtonGroup>
              </PasswordStep>
            )}

            {step === 'twoFactor' && (
              <PasswordStep>
                <TwoFactorIntro>
                  Enter the 6-digit code from Google Authenticator or Microsoft Authenticator.
                </TwoFactorIntro>
                {!useRecoveryCode ? (
                  <ModernInput
                    autoFocus
                    label='Authentication code'
                    name='code'
                    value={values.code}
                    onChange={handleOnChange}
                    onKeyDown={handleKeyDown}
                    error={errors.code}
                  />
                ) : (
                  <ModernInput
                    autoFocus
                    label='Recovery code'
                    name='recoveryCode'
                    value={values.recoveryCode}
                    onChange={handleOnChange}
                    onKeyDown={handleKeyDown}
                    error={errors.recoveryCode}
                  />
                )}

                <ForgotPasswordWrapper>
                  <ForgotPasswordLink onClick={() => setUseRecoveryCode(!useRecoveryCode)}>
                    {useRecoveryCode ? 'Use authenticator code instead' : 'Use a recovery code'}
                  </ForgotPasswordLink>
                </ForgotPasswordWrapper>

                <RememberBrowserGroup>
                  <RememberBrowserTitle>Two-factor sign-in on this browser</RememberBrowserTitle>
                  <RememberOption>
                    <input
                      type='radio'
                      name='rememberBrowserWeeks'
                      checked={values.rememberBrowserWeeks === ''}
                      onChange={() => handleOnChange('rememberBrowserWeeks', '')}
                    />
                    Every sign-in
                  </RememberOption>
                  {[1, 2, 4].map(weeks => (
                    <RememberOption key={weeks}>
                      <input
                        type='radio'
                        name='rememberBrowserWeeks'
                        checked={String(values.rememberBrowserWeeks) === String(weeks)}
                        onChange={() => handleOnChange('rememberBrowserWeeks', weeks)}
                      />
                      Skip 2FA on this browser for {weeks} {weeks === 1 ? 'week' : 'weeks'}
                    </RememberOption>
                  ))}
                </RememberBrowserGroup>

                <ButtonGroup>
                  <BackButton type='button' onClick={handlePrevStep}>
                    <IconArrowLeft /> Back
                  </BackButton>
                  <PrimaryButton type='submit'>Verify</PrimaryButton>
                </ButtonGroup>
              </PasswordStep>
            )}
          </FormContent>
        </LoginForm>

        <Divider>
          <span>{t('login.noAccount')}</span>
        </Divider>

        <SignUpButton onClick={() => redirect(PATHS.signup)}>
          {t('login.labels.createAccount')}
        </SignUpButton>
      </LoginCard>
    </PageWrapper>
  )
}

export default Login

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

const PageWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.backgroundColor};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 24px 16px;
  min-height: ${({ theme }) =>
    `calc(100vh - ${theme.dimensions.topBarHeight})`};
  height: auto;
  overflow: hidden;

  background-image: url(${BackgroundImage});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  @media (max-width: 768px) {
    justify-content: center;
    align-items: center;
    padding: 16px 16px;

    background-size: contain;
    background-position: bottom center;
  }

  @media (min-width: 769px) {
    padding: 40px 24px;
  }
`

const LoginCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 480px;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 40px;
  border-radius: 12px;
  box-shadow:
    0 10px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 1;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow:
      0 15px 50px rgba(0, 0, 0, 0.15),
      0 0 0 1px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 992px) {
    padding: 32px 24px;
  }

  @media (max-width: 576px) {
    padding: 24px 20px;
    max-width: 100%;
    border-radius: 16px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);

    &:hover {
      transform: none;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
    }
  }
`

const LogoSection = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;

  img {
    width: auto;
    height: 34px;
    cursor: default;
  }
`

const HeaderText = styled.h1`
  font-size: 32px;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 32px;
  text-align: center;
  animation: ${fadeIn} 0.6s ease;

  span {
    display: block;
    font-weight: bold;
    color: ${({ theme }) => theme.colors.haiti};
    margin-top: 8px;
  }

  @media (max-width: 576px) {
    font-size: 26px;
    margin-bottom: 24px;
  }
`

const LoginForm = styled.form`
  box-sizing: border-box;
  width: 100%;
`

const FormContent = styled.div`
  animation: ${fadeIn} 0.3s ease;
`

const EmailStep = styled.div`
  margin-bottom: 24px;
`

const PasswordStep = styled.div`
  margin-bottom: 24px;
`

const TwoFactorIntro = styled.div`
  color: #374151;
  font-size: 14px;
  line-height: 22px;
  margin-bottom: 18px;
`

const RememberBrowserGroup = styled.div`
  background: #f7f8fd;
  border: 1px solid #e7ebf5;
  border-radius: 8px;
  display: grid;
  gap: 10px;
  margin: 0 0 24px;
  padding: 14px;
`

const RememberBrowserTitle = styled.div`
  color: #110c2f;
  font-size: 13px;
  font-weight: 700;
`

const RememberOption = styled.label`
  align-items: center;
  color: #374151;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  gap: 8px;
  line-height: 18px;

  input {
    accent-color: #36becd;
    flex: 0 0 auto;
  }
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid
  ${props => (props.$hasError ? '#cd2000' : props.$isValid ? '#36becd' : '#cdd4ea')};
  border-radius: 8px;
  padding: 0 12px;
  height: 48px;
  transition: all 0.2s;
  overflow: hidden;

  &:focus-within {
    border-color: ${props => (props.$hasError ? '#cd2000' : '#36becd')};
    box-shadow: 0 0 0 3px
    ${props =>
      props.$hasError ? 'rgba(205, 32, 0, 0.1)' : 'rgba(54, 190, 205, 0.1)'};
  }
`

const StyledInput = styled.input`
  border: none;
  background: transparent;
  width: 100%;
  height: 100%;
  font-size: 16px;
  color: #111827;
  outline: none;

  &[type="password"] {
    font-family: Verdana, sans-serif;
    letter-spacing: 0.125em;
    font-size: 16px;
  }

  &::placeholder {
    color: #9ca3af;
    font-family: inherit;
    letter-spacing: normal;
    font-size: 16px;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    transition: background-color 9999s ease-in-out 0s;
    -webkit-text-fill-color: #111827 !important;
    background-color: transparent !important;
  }
`

const RightSlot = styled.div`
  margin-left: 8px;
  display: flex;
  align-items: center;
  font-size: 14px;
`

const ErrorMessage = styled.div`
  font-size: 12px;
  color: #cd2000;
  margin-top: 4px;
`

const EyeToggle = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`

const SharedButtonStyle = css`
  width: 100%;
  height: 52px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  transition: all 0.2s ease;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`


const PrimaryButton = styled.button`
  ${SharedButtonStyle}
  background: #110c2f;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #36becd;
    transform: translateY(-1px);
  }
`


const SignUpButton = styled.button`
  ${SharedButtonStyle}
  background: transparent;
  color: #110c2f;
  border: 2px solid #110c2f;
  box-shadow: none;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 400px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`

const BackButton = styled.button`
  height: 48px;
  padding: 0 16px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
  }
`

const ForgotPasswordWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  margin-top: -15px;
`

const ForgotPasswordLink = styled.span`
  color: #36becd;
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;

  &:hover {
    text-decoration: underline;
    color: #0c132b;
  }
`

const Divider = styled.div`
  position: relative;
  width: 100%;
  text-align: center;
  margin: 32px 0;

  span {
    position: relative;
    z-index: 2;
    padding: 0 16px;
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.haiti};
    font-size: 14px;
  }

  &::before {
    content: '';
    width: 100%;
    height: 1px;
    background: linear-gradient(
      to right,
      ${({ theme }) => theme.colors.lighterGrey},
      ${({ theme }) => theme.colors.primary}40,
      ${({ theme }) => theme.colors.lighterGrey}
    );
    position: absolute;
    left: 0;
    top: 50%;
    z-index: 1;
  }
`
